import { BehaviorSubject, map, Observable, of } from 'rxjs';

import { ExportEntity } from '../../entities/downloads/export-entity.entity';
import { Comparable } from '../../record/entities/misc/comparable.interface';
import { Trackable } from '../../record/entities/misc/trackable.interface';
import { classifyGap, GapKind } from '../helpers/download-gap.helper';
import { parseExportName, toPartName } from '../helpers/download-grouping.helper';
import { classifyExportStatus, ExportStatusClass } from '../helpers/export-status.helper';

export interface Message {
    readonly key: string;
    readonly params?: Record<string, string | number>;
}

export interface NaamCellData {
    readonly title: string;
    readonly subtitle: Message | null;
}

export type ProgressTint = 'update' | 'success' | 'warning' | 'error';

export interface StatusCellData {
    readonly label: Message;
    readonly progress: number;
    readonly tint: ProgressTint;
}

export interface FoutenCellData {
    readonly lines: readonly Message[];
}

export interface DownloadFile {
    readonly url: string;
    readonly filename: string;
}

export interface DownloadTarget {
    readonly files: readonly DownloadFile[];
    readonly label: Message;
}

const STATUS_PREFIX: string = 'downloads.status';
const GROUP_STATUS_PREFIX: string = 'downloads.status.group';
const GROUP_PREFIX: string = 'downloads.group';
const MESSAGE_PREFIX: string = 'downloads.messages';

/** The tint of a deel that does not exist yet: the wait of a made-but-unstarted deel. */
const PENDING_STATUS: string = 'Waiting';

export abstract class DownloadRow implements Comparable, Trackable {
    abstract readonly key: string;

    abstract readonly naam$: Observable<NaamCellData>;
    abstract readonly status$: Observable<StatusCellData>;
    abstract readonly downloaddatum$: Observable<string>;
    abstract readonly vervaldatum$: Observable<string>;
    abstract readonly fouten$: Observable<FoutenCellData>;
    abstract readonly download$: Observable<DownloadTarget | null>;

    equals(other: any): boolean {
        return other instanceof DownloadRow && other.key === this.key;
    }

    trackBy(): string {
        return this.key;
    }
}

export function toStatusLabel(status: string): Message {
    const value: string = (status ?? '').toLowerCase();

    switch (classifyExportStatus(status)) {
        case 'open':
            return { key: `${STATUS_PREFIX}.${value === 'waiting' ? 'Waiting' : 'InProgress'}` };
        case 'klaar':
            return { key: `${STATUS_PREFIX}.Completed` };
        default:
            return { key: `${STATUS_PREFIX}.Failed` };
    }
}

function toStatusTint(status: string): ProgressTint {
    switch (classifyExportStatus(status)) {
        case 'open':
            return (status ?? '').toLowerCase() === 'waiting' ? 'warning' : 'update';
        case 'klaar':
            return 'success';
        default:
            return 'error';
    }
}

export class DownloadJobRow extends DownloadRow {
    readonly key: string;

    private readonly job$: BehaviorSubject<ExportEntity>;

    readonly naam$: Observable<NaamCellData>;
    readonly status$: Observable<StatusCellData>;
    readonly downloaddatum$: Observable<string>;
    readonly vervaldatum$: Observable<string>;
    readonly fouten$: Observable<FoutenCellData>;
    readonly download$: Observable<DownloadTarget | null>;

    constructor(job: ExportEntity) {
        super();

        this.key = job.ExportJobId;
        this.job$ = new BehaviorSubject<ExportEntity>(job);

        this.naam$ = this.job$.pipe(map(current => ({ title: current.Name, subtitle: null })));

        this.status$ = this.job$.pipe(
            map(current => ({
                label: toStatusLabel(current.Status),
                progress: classifyExportStatus(current.Status) === 'mislukt' ? 1 : (current.Progress ?? 0),
                tint: toStatusTint(current.Status)
            }))
        );

        this.downloaddatum$ = this.job$.pipe(map(current => current.CreationDate));
        this.vervaldatum$ = this.job$.pipe(map(current => current.ExpiryDate));

        this.fouten$ = this.job$.pipe(
            map(current => ({
                lines: classifyExportStatus(current.Status) === 'mislukt' ? [{ key: `${MESSAGE_PREFIX}.error` }] : []
            }))
        );

        this.download$ = this.job$.pipe(map(current => toJobTarget(current)));
    }

    update(job: ExportEntity): void {
        this.job$.next(job);
    }

    get job(): ExportEntity {
        return this.job$.value;
    }

    get statusClass(): ExportStatusClass {
        return classifyExportStatus(this.job$.value.Status);
    }

    get partNumber(): number {
        return parseExportName(this.job$.value.Name)?.part ?? 0;
    }
}

function toJobTarget(job: ExportEntity): DownloadTarget | null {
    if (!job.DownloadUrl) {
        return null;
    }

    return {
        files: [{ url: job.DownloadUrl, filename: job.Name }],
        label: { key: 'downloads.actions.download', params: { name: job.Name } }
    };
}

/**
 * A deel the export will have and does not have yet: the list cannot serve what the backend has not made, so
 * this stands in its place until it does. It is a record row rather than an inline message because it has to
 * fill the same columns every other deel fills — its name, and a bar saying it is still coming.
 *
 * Every stream is a constant. Nothing about a deel that does not exist changes; when it starts existing it
 * arrives as a {@link DownloadJobRow} and this row is dropped.
 */
export class PendingPartRow extends DownloadRow {
    readonly key: string;

    readonly naam$: Observable<NaamCellData>;
    readonly status$: Observable<StatusCellData>;
    readonly downloaddatum$: Observable<string>;
    readonly vervaldatum$: Observable<string>;
    readonly fouten$: Observable<FoutenCellData>;
    readonly download$: Observable<DownloadTarget | null>;

    constructor(
        readonly groupKey: string,
        readonly partNumber: number,
        total: number
    ) {
        super();

        this.key = pendingPartKey(groupKey, partNumber);

        this.naam$ = of({ title: toPartName(groupKey, partNumber, total), subtitle: null });

        // A placeholder, not a deel the backend reported: its status is not known, so it says so.
        this.status$ = of({
            label: { key: `${STATUS_PREFIX}.unknown` },
            progress: 0,
            tint: toStatusTint(PENDING_STATUS)
        });

        // No dates and no fouten: it has neither yet, and the cells render these as a dash and as nothing.
        this.downloaddatum$ = of('');
        this.vervaldatum$ = of('');
        this.fouten$ = of({ lines: [] });
        this.download$ = of(null);
    }
}

/** Distinct from any ExportJobId, and stable across polls so the row is not remounted under the reader. */
export function pendingPartKey(groupKey: string, part: number): string {
    return `pending:${groupKey}:${part}`;
}

interface GroupState {
    readonly total: number;
    readonly parts: DownloadJobRow[];
    /** Whether the page held the whole list when this state was built. See {@link classifyGap}. */
    readonly complete: boolean;
}

export class DownloadGroupRow extends DownloadRow {
    readonly key: string;

    private readonly state$: BehaviorSubject<GroupState>;

    readonly naam$: Observable<NaamCellData>;
    readonly status$: Observable<StatusCellData>;
    readonly downloaddatum$: Observable<string>;
    readonly vervaldatum$: Observable<string>;
    readonly fouten$: Observable<FoutenCellData>;
    readonly download$: Observable<DownloadTarget | null>;

    constructor(key: string, total: number, parts: DownloadJobRow[], complete: boolean) {
        super();

        this.key = key;
        this.state$ = new BehaviorSubject<GroupState>({ total, parts, complete });

        this.naam$ = this.state$.pipe(
            map(state => ({
                title: this.key,
                subtitle: { key: `${GROUP_PREFIX}.delen`, params: { n: state.total } }
            }))
        );

        this.status$ = this.state$.pipe(map(state => toGroupStatus(state, gapKindOf(this.key, state))));

        this.downloaddatum$ = this.state$.pipe(map(state => earliest(state.parts.map(part => part.job.CreationDate))));
        this.vervaldatum$ = this.state$.pipe(map(state => earliest(state.parts.map(part => part.job.ExpiryDate))));

        this.fouten$ = this.state$.pipe(map(state => toGroupFouten(state, gapKindOf(this.key, state))));

        this.download$ = this.state$.pipe(map(state => toGroupTarget(this.key, state.total, state.parts)));
    }

    update(total: number, parts: DownloadJobRow[], complete: boolean): void {
        this.state$.next({ total, parts, complete });
    }

    get parts(): DownloadJobRow[] {
        return this.state$.value.parts;
    }

    get total(): number {
        return this.state$.value.total;
    }

    get missing(): number {
        return Math.max(this.total - this.parts.length, 0);
    }

    /** What the delen this group is short of are: still being made, or lost. One verdict for the whole group. */
    get gapKind(): GapKind {
        return gapKindOf(this.key, this.state$.value);
    }

    get missingParts(): number[] {
        const present: Set<number> = new Set(this.parts.map(part => part.partNumber));

        return Array.from({ length: this.total }, (_, index) => index + 1).filter(part => !present.has(part));
    }

    get rankDate(): string {
        return earliest(this.parts.map(part => part.job.CreationDate));
    }
}

function earliest(dates: string[]): string {
    return dates.filter(date => !!date).sort()[0] ?? '';
}

function gapKindOf(key: string, state: GroupState): GapKind {
    return classifyGap({
        key,
        statuses: state.parts.map(part => part.statusClass),
        expired: state.parts.some(part => part.job.Expired),
        complete: state.complete
    });
}

function toGroupStatus({ total, parts }: GroupState, gap: GapKind): StatusCellData {
    const open: number = parts.filter(part => part.statusClass === 'open').length;
    const done: number = parts.filter(part => part.statusClass === 'klaar').length;
    const failed: number = parts.filter(part => part.statusClass === 'mislukt').length;
    const missing: number = Math.max(total - parts.length, 0);
    const pending: number = gap === 'pending' ? missing : 0;

    // A deel still to come is as much work outstanding as a deel still running, and reads the same way.
    if (open > 0 || pending > 0) {
        return {
            label: { key: `${GROUP_STATUS_PREFIX}.busy`, params: { done, total } },
            progress: progressOver(total, parts),
            tint: 'update'
        };
    }

    if (missing > 0) {
        return {
            label: { key: `${GROUP_STATUS_PREFIX}.available`, params: { available: parts.length, total } },
            progress: 1,
            tint: 'warning'
        };
    }

    if (failed > 0 && done > 0) {
        return {
            label: { key: `${GROUP_STATUS_PREFIX}.partial`, params: { done, total, failed } },
            progress: 1,
            tint: 'warning'
        };
    }

    if (failed > 0) {
        return { label: { key: `${GROUP_STATUS_PREFIX}.failed` }, progress: 1, tint: 'error' };
    }

    return { label: { key: `${GROUP_STATUS_PREFIX}.done` }, progress: 1, tint: 'success' };
}

/**
 * Over `total`, not over the delen present: averaging the ones that are there puts the bar at full while four
 * fifths of the export is still unwritten.
 */
function progressOver(total: number, parts: DownloadJobRow[]): number {
    if (total <= 0) {
        return 0;
    }

    return parts.reduce((sum, part) => sum + (part.job.Progress ?? 0), 0) / total;
}

function toGroupFouten({ total, parts }: GroupState, gap: GapKind): FoutenCellData {
    const failed: number = parts.filter(part => part.statusClass === 'mislukt').length;
    // Only a gap we have given up on is a fout. One still being made is progress, and gets no recourse offered.
    const gone: number = gap === 'gone' ? Math.max(total - parts.length, 0) : 0;

    if (failed === 0 && gone === 0) {
        return { lines: [] };
    }

    const counts: Message[] = [
        ...(failed > 0 ? [{ key: `${MESSAGE_PREFIX}.group-failed`, params: { failed, total } }] : []),
        ...(gone > 0 ? [{ key: `${MESSAGE_PREFIX}.group-missing`, params: { missing: gone } }] : [])
    ];

    const advice: Message =
        failed > 0
            ? { key: `${MESSAGE_PREFIX}.group-contact-admin` }
            : { key: `${MESSAGE_PREFIX}.group-export-again` };

    return { lines: [...counts, advice] };
}

function toGroupTarget(key: string, total: number, parts: DownloadJobRow[]): DownloadTarget | null {
    const complete: boolean =
        parts.length === total && parts.every(part => part.statusClass === 'klaar' && !!part.job.DownloadUrl);

    if (!complete) {
        return null;
    }

    return {
        files: parts.map(part => ({ url: part.job.DownloadUrl!, filename: part.job.Name })),
        label: { key: 'downloads.actions.download-group', params: { name: key, count: total } }
    };
}
